import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check, ChevronLeft, ChevronRight, CircleAlert, CopyPlus, ExternalLink, GripVertical, ImagePlus, Loader2, Search,
  Square, Star, Upload, X,
} from 'lucide-react';
import {
  apiError, copyPagePhotos, getCopyJob, getProduct, getScrape, listScrapes, removeProductImage,
  reorderProductImages, startCopyJob, uploadProductImages,
} from '../../api/catalogue';
import {
  CATALOGUE_CSS, Notice, PageHeader, PrimaryButton, RUN_END, SecondaryButton, Thumb, ago, elapsed, focusRing,
  isCopyJob, looksLikeImage, num, thumbOf, useScrapeRun,
} from './shared';

// Catalogue -> Images: the photos customers will see for each design. Photos
// uploaded here go to S3 and onto every variant of the design
// (routers/catalogue_products.py); the first is the cover. The page's and
// ZOCS's own photos are shown beside them for reference.

const PAGE = 50;
const GOOD = 3;                          // photos a design should have
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_MB = 5;
const MAX_FILES = 10;

function PhotoCount({ n }) {
  const ok = n >= GOOD;
  return (
    <span className={`inline-flex items-center h-5 px-1.5 rounded text-[11px] font-medium tabular-nums ring-1 ring-inset ${ok ? 'bg-green-50 text-green-700 ring-green-200' : n ? 'bg-amber-50 text-amber-700 ring-amber-200' : 'bg-gray-50 text-gray-500 ring-gray-200'}`}>
      {n} photo{n === 1 ? '' : 's'}
    </span>
  );
}

function DesignList({ list, selected, onSelect, query, setQuery, page, setPage }) {
  const total = list?.total ?? 0;
  return (
    <aside className="rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col min-h-0">
      <div className="p-3 border-b border-gray-100">
        <label className="flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-200 focus-within:ring-2 focus-within:ring-zoddle-green/30">
          <Search size={14} className="text-gray-400 shrink-0" aria-hidden />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search design code or name"
            className="flex-1 min-w-0 text-[13px] outline-none bg-transparent placeholder:text-gray-400" />
          {query && <button onClick={() => setQuery('')} aria-label="Clear search" className="text-gray-400 hover:text-gray-600"><X size={14} /></button>}
        </label>
      </div>
      <div className="flex-1 overflow-y-auto">
        {!list && <p className="p-4 text-[12.5px] text-gray-400">Loading…</p>}
        {list && list.rows.length === 0 && <p className="p-4 text-[12.5px] text-gray-400">No designs match.</p>}
        {list && list.rows.map((r) => (
          <button key={r.design_id} onClick={() => onSelect(r.design_id)}
            className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 transition-colors ${selected === r.design_id ? 'bg-sky-50' : 'hover:bg-gray-50'} ${focusRing} focus-visible:ring-inset`}>
            <Thumb url={r.image || (looksLikeImage(r.image_url_1) ? r.image_url_1 : null)} />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-gray-900 truncate">
                <span className="font-mono text-gray-400 mr-1.5">{r.design_code}</span>{r.title || r.design_name}
              </p>
              <p className="text-[12px] text-gray-500 truncate">{r.brand}</p>
            </div>
            <PhotoCount n={r.catalogue_photos ?? 0} />
          </button>
        ))}
      </div>
      {total > PAGE && (
        <div className="px-3 py-2 border-t border-gray-100 flex items-center justify-between text-[12px] text-gray-500">
          <span className="tabular-nums">{num(page * PAGE + 1)}–{num(Math.min(total, (page + 1) * PAGE))} of {num(total)}</span>
          <span className="flex gap-1.5">
            <SecondaryButton className="h-7 px-2" onClick={() => setPage(page - 1)} disabled={page === 0} aria-label="Previous page"><ChevronLeft size={14} /></SecondaryButton>
            <SecondaryButton className="h-7 px-2" onClick={() => setPage(page + 1)} disabled={(page + 1) * PAGE >= total} aria-label="Next page"><ChevronRight size={14} /></SecondaryButton>
          </span>
        </div>
      )}
    </aside>
  );
}

function ReferencePhotos({ title, urls }) {
  if (!urls.length) return null;
  return (
    <section className="mt-6">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title} · {urls.length}</h3>
      <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
        {urls.slice(0, 24).map((u) => (
          <a key={u} href={u} target="_blank" rel="noreferrer" title="Open full size"
            className={`block rounded-lg overflow-hidden ring-1 ring-gray-200 bg-gray-50 ${focusRing}`}>
            <img src={thumbOf(u, 240)} alt="" loading="lazy" referrerPolicy="no-referrer" className="aspect-square w-full object-cover" />
          </a>
        ))}
      </div>
    </section>
  );
}

// The scraped page's photos, each copyable into the catalogue (S3).
function PagePhotos({ urls, copied, busy, copying, onCopy }) {
  if (!urls.length) return null;
  const left = urls.filter((u) => !copied.has(u));
  return (
    <section className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          On the product page · {urls.length}{copied.size ? ` · ${urls.length - left.length} copied` : ''}
        </h3>
        {left.length > 0 && (
          <SecondaryButton className="h-8" onClick={() => onCopy(null)} disabled={busy}>
            {copying === 'all' ? <Loader2 size={14} className="motion-safe:animate-spin" /> : <CopyPlus size={14} />}
            Copy {left.length === urls.length ? 'all' : `the other ${left.length}`} to catalogue
          </SecondaryButton>
        )}
      </div>
      <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-2">
        {urls.slice(0, 24).map((u) => {
          const done = copied.has(u);
          return (
            <div key={u} className="relative rounded-lg overflow-hidden ring-1 ring-gray-200 bg-gray-50">
              <a href={u} target="_blank" rel="noreferrer" title="Open full size" className={`block ${focusRing}`}>
                <img src={thumbOf(u, 240)} alt="" loading="lazy" referrerPolicy="no-referrer" className="aspect-square w-full object-cover" />
              </a>
              {done ? (
                <span className="absolute right-1 top-1 h-6 w-6 rounded-md bg-zoddle-green text-white flex items-center justify-center" title="In the catalogue">
                  <Check size={14} />
                </span>
              ) : (
                <button onClick={() => onCopy([u])} disabled={busy} aria-label="Copy this photo to the catalogue" title="Copy to catalogue"
                  className={`absolute right-1 top-1 h-6 w-6 rounded-md bg-white/90 text-gray-600 hover:text-zoddle-green shadow-sm flex items-center justify-center disabled:opacity-50 ${focusRing}`}>
                  {copying === u ? <Loader2 size={13} className="motion-safe:animate-spin" /> : <CopyPlus size={13} />}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProductPanel({ designId, onChanged }) {
  const [product, setProduct] = useState(null);
  const [scrape, setScrape] = useState(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);     // null, or 0..100 while uploading
  const [removing, setRemoving] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [copying, setCopying] = useState(null);       // 'all' or a url while copying
  const [note, setNote] = useState('');
  const [dragFrom, setDragFrom] = useState(null);      // index of the photo being dragged
  const [dragTo, setDragTo] = useState(null);
  const input = useRef(null);

  useEffect(() => {
    let live = true;
    setProduct(null); setScrape(null); setError('');
    Promise.all([getProduct(designId), getScrape(designId).catch(() => null)])
      .then(([p, s]) => { if (live) { setProduct(p); setScrape(s); } })
      .catch((e) => live && setError(apiError(e, 'Could not load this design')));
    return () => { live = false; };
  }, [designId]);

  const upload = useCallback(async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const bad = files.find((f) => !TYPES.includes(f.type));
    if (bad) { setError(`${bad.name} is not a JPEG, PNG or WebP photo.`); return; }
    const big = files.find((f) => f.size > MAX_MB * 1024 * 1024);
    if (big) { setError(`${big.name} is over ${MAX_MB} MB.`); return; }
    if (files.length > MAX_FILES) { setError(`Add at most ${MAX_FILES} photos at a time.`); return; }
    setError(''); setProgress(0);
    try {
      setProduct(await uploadProductImages(designId, files, setProgress));
      onChanged();
    } catch (e) {
      setError(apiError(e, 'Could not upload the photos'));
    } finally {
      setProgress(null);
      if (input.current) input.current.value = '';
    }
  }, [designId, onChanged]);

  async function copy(urls) {
    setCopying(urls ? urls[0] : 'all'); setError(''); setNote('');
    try {
      const r = await copyPagePhotos(designId, urls);
      setProduct(r);
      onChanged();
      if (r.failed?.length) setError(`${r.failed.length} photo${r.failed.length === 1 ? '' : 's'} could not be copied: ${r.failed[0].why}.`);
      else if (r.copied) setNote(`Copied ${r.copied} photo${r.copied === 1 ? '' : 's'} to the catalogue.`);
    } catch (e) {
      setError(apiError(e, 'Could not copy the photos'));
    } finally {
      setCopying(null);
    }
  }

  // Move one photo to another place; the new order is saved for every size.
  async function move(from, to) {
    const list = product?.variants?.[0]?.images || [];
    if (from === null || to === null || from === to || !list[from]) return;
    const next = [...list];
    const [p] = next.splice(from, 1);
    next.splice(to, 0, p);
    const before = product;
    setProduct({ ...product, variants: product.variants.map((x) => ({ ...x, images: next })) });   // show it now
    setError(''); setNote('');
    try {
      setProduct(await reorderProductImages(designId, next.map((x) => x.url)));
      onChanged();
    } catch (e) {
      setProduct(before);
      setError(apiError(e, 'Could not save the new order'));
    }
  }

  async function remove(url) {
    setRemoving(url); setError('');
    try {
      setProduct(await removeProductImage(designId, url));
      onChanged();
    } catch (e) {
      setError(apiError(e, 'Could not remove the photo'));
    } finally {
      setRemoving(null);
    }
  }

  if (error && !product) return <Notice icon={CircleAlert}>{error}</Notice>;
  if (!product) return <p className="text-[12.5px] text-gray-400">Loading…</p>;

  const v = product.variants;
  const photos = v[0]?.images || [];
  const src = scrape?.data?.sources?.[scrape?.data?.primary];
  const busy = progress !== null || copying !== null;
  const copied = new Set(photos.map((p) => p.source_url).filter(Boolean));

  return (
    <div>
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-gray-900 leading-snug">
            <span className="font-mono text-gray-400 mr-1.5">{product.design_code}</span>{v[0]?.title || scrape?.design_name}
          </p>
          <p className="mt-1 text-[12.5px] text-gray-500">
            {scrape?.brand}{v[0]?.color ? ` · ${v[0].color}` : ''}
          </p>
          {v.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Variants">
              {v.map((x) => (
                <span key={x.variant_id}
                  title={`SKU ${x.sku_code || '—'} · ${x.barcodes?.length || 0} unit${x.barcodes?.length === 1 ? '' : 's'}: ${(x.barcodes || []).join(', ') || 'none'}${x.brand_barcodes?.length ? ` · brand: ${x.brand_barcodes.join(', ')}` : ''}`}
                  className="inline-flex items-center h-6 px-2 rounded-md bg-gray-50 ring-1 ring-inset ring-gray-200 text-[12px] text-gray-700">{x.size}</span>
              ))}
            </div>
          )}
        </div>
        <PhotoCount n={photos.length} />
      </header>

      {v.length === 0 ? (
        <Notice tone="gray" icon={CircleAlert} className="mt-5">This design has no sizes in ZOCS, so it has no variants to add photos to.</Notice>
      ) : (
        <section className="mt-5">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Catalogue photos</h3>
          <p className="mt-0.5 text-[12px] text-gray-400">
            The first is the cover — drag photos to change the order. Every size of this design shows them{photos.length < GOOD ? `; aim for at least ${GOOD}` : ''}.
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((p, i) => (
              <figure key={p.url}
                draggable={!busy && photos.length > 1}
                onDragStart={(e) => { setDragFrom(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', p.url); }}
                onDragOver={(e) => { if (dragFrom !== null) { e.preventDefault(); setDragTo(i); } }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); move(dragFrom, i); setDragFrom(null); setDragTo(null); }}
                onDragEnd={() => { setDragFrom(null); setDragTo(null); }}
                className={`relative rounded-xl overflow-hidden ring-1 bg-gray-50 group transition ${dragTo === i && dragFrom !== i ? 'ring-2 ring-zoddle-green' : 'ring-gray-200'} ${dragFrom === i ? 'opacity-40' : ''} ${photos.length > 1 ? 'cursor-grab active:cursor-grabbing' : ''}`}>
                <img src={p.url} alt="" loading="lazy" draggable={false} className="aspect-[4/5] w-full object-cover pointer-events-none" />
                {i === 0
                  ? <span className="absolute left-2 top-2 h-6 px-2 inline-flex items-center rounded-md bg-gray-900/80 text-white text-[11.5px] font-medium">Cover</span>
                  : (
                    <button onClick={() => move(i, 0)} disabled={busy} title="Make this the cover"
                      className={`absolute left-2 top-2 h-6 px-2 inline-flex items-center gap-1 rounded-md bg-white/90 text-[11.5px] font-medium text-gray-700 shadow-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-50 ${focusRing}`}>
                      <Star size={12} aria-hidden /> Make cover
                    </button>
                  )}
                {photos.length > 1 && (
                  <span className="absolute left-2 bottom-9 h-6 w-6 rounded-md bg-white/80 text-gray-500 flex items-center justify-center opacity-0 group-hover:opacity-100" aria-hidden>
                    <GripVertical size={14} />
                  </span>
                )}
                <button onClick={() => remove(p.url)} disabled={busy || removing === p.url} aria-label="Remove this photo"
                  className={`absolute right-2 top-2 h-7 w-7 rounded-lg bg-white/90 text-gray-600 hover:text-zoddle-pink shadow-sm flex items-center justify-center disabled:opacity-50 ${focusRing}`}>
                  {removing === p.url ? <Loader2 size={14} className="motion-safe:animate-spin" /> : <X size={15} />}
                </button>
                <figcaption className="px-2 py-1.5 text-[11px] text-gray-400 truncate bg-white">
                  {p.source === 'scraped' ? 'From the product page' : 'Uploaded'} · {p.added_by}
                </figcaption>
              </figure>
            ))}
            <label
              onDragOver={(e) => { if (dragFrom !== null) return; e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (!busy) upload(e.dataTransfer.files); }}
              className={`aspect-[4/5] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-center px-3 cursor-pointer transition-colors ${dragOver ? 'border-zoddle-green bg-green-50' : 'border-gray-300 bg-white hover:border-gray-400'} ${busy ? 'pointer-events-none opacity-70' : ''} focus-within:ring-2 focus-within:ring-zoddle-green/40`}>
              {busy ? (
                <>
                  <Loader2 size={20} className="text-gray-400 motion-safe:animate-spin" />
                  <span className="text-[12.5px] text-gray-600 tabular-nums">Uploading… {progress}%</span>
                </>
              ) : (
                <>
                  <Upload size={20} className="text-gray-400" aria-hidden />
                  <span className="text-[12.5px] font-medium text-gray-700">Add photos</span>
                  <span className="text-[11.5px] text-gray-400">Drop here or click · JPEG, PNG or WebP, up to {MAX_MB} MB</span>
                </>
              )}
              <input ref={input} type="file" accept={TYPES.join(',')} multiple className="sr-only"
                onChange={(e) => upload(e.target.files)} disabled={busy} />
            </label>
          </div>
          {error && <Notice icon={CircleAlert} className="mt-3">{error}</Notice>}
          {note && <Notice tone="green" icon={Check} className="mt-3">{note}</Notice>}
        </section>
      )}

      {v.length > 0 && <PagePhotos urls={src?.images || []} copied={copied} busy={busy} copying={copying} onCopy={copy} />}
      <ReferencePhotos title="In ZOCS now" urls={(scrape?.images_now || []).filter(looksLikeImage)} />
      {src?.url && (
        <a href={src.url} target="_blank" rel="noreferrer"
          className={`mt-4 inline-flex items-center gap-1 text-[12px] font-medium text-zoddle-blue hover:underline rounded ${focusRing}`}>
          Open the product page <ExternalLink size={12} />
        </a>
      )}
    </div>
  );
}

// Copy every scraped design's page photos into S3, in the background (one
// catalogue job at a time, shared with scrape runs).
function CopyJob({ onFinished }) {
  const [left, setLeft] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const loadLeft = useCallback(() => { getCopyJob().then((r) => setLeft(r.designs_left)).catch(() => {}); }, []);
  const job = useScrapeRun((what) => { if (what === 'finished') { loadLeft(); onFinished(); } else onFinished(); });
  useEffect(() => { loadLeft(); }, [loadLeft]);

  async function start() {
    setStarting(true); setError('');
    try { await startCopyJob(); await job.refresh(); } catch (e) { setError(apiError(e, 'Could not start the copy')); }
    finally { setStarting(false); }
  }

  const run = job.run;
  const copying = job.running && isCopyJob(run);
  return (
    <section className="mt-6 rounded-xl border border-gray-200 bg-white px-5 py-4">
      {copying ? (
        <>
          <p className="text-[13px] text-gray-600">
            <span className="text-[20px] font-semibold text-gray-900 tabular-nums">{num(run.total)}</span>
            <span className="ml-1.5">designs getting their page photos copied to S3</span>
            <span className="float-right text-[12px] text-gray-400 tabular-nums">{elapsed(run.started_at)}</span>
          </p>
          <div className="mt-2.5 h-2 rounded-full bg-sky-100 overflow-hidden" aria-hidden><div className="h-full w-full catalogue-stream" /></div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <SecondaryButton tone="pink" onClick={job.stop} disabled={job.busy || run.stop_requested}>
              <Square size={12} className="fill-current" />{run.stop_requested ? 'Stopping…' : 'Stop'}
            </SecondaryButton>
            <span className="text-[12px] text-gray-400">Started by {run.started_by} · you can leave this page</span>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[13px] text-gray-600">
            {left === null ? 'Checking…' : left === 0
              ? 'Every scraped design’s page photos are in the catalogue.'
              : <><b className="text-gray-900 tabular-nums">{num(left)}</b> scraped design{left === 1 ? '' : 's'} have page photos not yet copied to S3.</>}
            {run && isCopyJob(run) && RUN_END[run.status] && (
              <span className="block text-[12px] text-gray-400">
                Last copy {RUN_END[run.status]} {ago(run.finished_at)} · {num(run.stats?.photos_copied)} photos for {num(run.stats?.designs_done)} designs
                {run.stats?.photos_failed ? ` · ${num(run.stats.photos_failed)} would not download` : ''}
              </span>
            )}
            {job.running && !isCopyJob(run) && <span className="block text-[12px] text-gray-400">A scrape run is going; the copy can start once it finishes.</span>}
          </div>
          {left > 0 && (
            <PrimaryButton onClick={start} disabled={starting || job.running}>
              {starting ? <Loader2 size={14} className="motion-safe:animate-spin" /> : <CopyPlus size={14} />}
              Copy them in the background
            </PrimaryButton>
          )}
        </div>
      )}
      {(error || job.error) && <p className="mt-2 text-[12.5px] text-zoddle-pink">{error || job.error}</p>}
    </section>
  );
}

export default function CatalogueImages() {
  const [list, setList] = useState(null);
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    try {
      const params = { offset: page * PAGE, limit: PAGE };
      if (q) params.q = q;
      setList(await listScrapes(params));
      setLoadError('');
    } catch (e) {
      setLoadError(apiError(e, 'Could not load the catalogue'));
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => { setQ(query.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [query]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1440px]">
      <style>{CATALOGUE_CSS}</style>
      <PageHeader title="Images">
        Add the photos customers will see for each design. They go to the media bucket and onto every size of the design; the first is the cover.
      </PageHeader>
      {loadError && <Notice icon={CircleAlert} className="mt-4">{loadError}</Notice>}
      <CopyJob onFinished={load} />
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-4 lg:h-[calc(100vh-280px)]">
        <DesignList list={list} selected={selected} onSelect={setSelected}
          query={query} setQuery={setQuery} page={page} setPage={setPage} />
        <main className="rounded-xl border border-gray-200 bg-white p-5 overflow-y-auto min-h-[320px]">
          {selected
            ? <ProductPanel designId={selected} onChanged={load} />
            : (
              <div className="h-full flex flex-col items-center justify-center text-center gap-2 py-16">
                <ImagePlus size={22} className="text-gray-300" aria-hidden />
                <p className="text-[13px] font-medium text-gray-700">Pick a design</p>
                <p className="text-[12.5px] text-gray-400">Its photos, sizes and upload box show here.</p>
              </div>
            )}
        </main>
      </div>
    </div>
  );
}
